import React, { useState, useMemo } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box, Flex, Grid, Text, Spinner, Table, Thead, Tbody, Tr, Th, Td, Badge, Button, Icon } from '@chakra-ui/react';
import { useQuery } from '@apollo/client';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import {
  Gauge,
  Queue,
  Lightning,
  ShieldCheck,
  ShieldWarning,
  Clock,
  ArrowsClockwise,
  Pulse,
  ChartLine,
  Warning,
  CheckCircle,
  XCircle,
} from '@phosphor-icons/react';

import Layout from '../../../components/Layout';
import { FilterSelectPill } from '../../../components/FilterPill/Select';
import { FilterDateRangePill } from '../../../components/FilterPill/DateRange';
import {
  GET_UPS_API_METRICS,
  OPERATION_LABELS,
  ENDPOINT_GROUPS,
  type GetUpsApiMetricsData,
  type GetUpsApiMetricsVars,
  type RecentRequest,
} from './query';

interface ApiMonitorProps extends RouteComponentProps {}

type TimePreset = '1h' | '6h' | '24h' | '7d';

const PRESET_CONFIG: Record<TimePreset, { label: string; ms: number; bucket: number }> = {
  '1h': { label: 'Last 1 hour', ms: 60 * 60 * 1000, bucket: 1 },
  '6h': { label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000, bucket: 5 },
  '24h': { label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000, bucket: 15 },
  '7d': { label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000, bucket: 60 },
};

const BUCKET_OPTIONS = [
  { value: 1, label: '1 min' },
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 60, label: '1 h' },
];

const CHART_COLORS = {
  success: '#10b981',
  failure: '#ef4444',
  total: '#6366f1',
  queued: '#a855f7',
  rateLimit: '#f59e0b',
  avgDuration: '#6366f1',
  grid: '#1e293b',
  text: '#94a3b8',
};

const cardProps = {
  bg: 'gray.800',
  borderWidth: '1px',
  borderColor: 'gray.700',
  rounded: 'md',
  p: '2.5',
} as const;

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms} ms`;
}

function ratePercent(current: number, max: number): number {
  return max > 0 ? Math.round((current / max) * 100) : 0;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="11px" fontWeight="medium" fontFamily="mono" textTransform="uppercase" letterSpacing="wider" color="gray.400">
      {children}
    </Text>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <Box {...cardProps} minW="0">
      <Flex gap="1.5" alignItems="center" mb="0.5">
        <Box color="gray.500" lineHeight="1">
          {icon}
        </Box>
        <Text fontSize="10px" fontWeight="medium" fontFamily="mono" textTransform="uppercase" letterSpacing="wider" color="gray.500">
          {label}
        </Text>
      </Flex>
      <Text fontSize="xl" fontWeight="semibold" color={color || 'white'} lineHeight="1.2" fontFamily="mono">
        {value}
      </Text>
      {sub && (
        <Text fontSize="11px" color="gray.500" mt="0.5">
          {sub}
        </Text>
      )}
    </Box>
  );
}

function LiveRequestRow({ req }: { req: RecentRequest }) {
  const opLabel = OPERATION_LABELS[req.operation] || req.operation;
  const time = new Date(req.createdAt);
  const timeStr = time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <Flex
      py="1"
      px="2"
      alignItems="center"
      gap="2"
      fontSize="12px"
      borderBottom="1px solid"
      borderColor="gray.800"
      _hover={{ bg: 'whiteAlpha.50' }}
      transition="background 100ms"
    >
      <Box flexShrink={0} color="gray.500" fontFamily="mono" fontSize="11px" w="55px">
        {timeStr}
      </Box>
      <Text color="gray.300" fontWeight="medium" isTruncated flex="1" fontSize="12px">
        {opLabel}
      </Text>
      {req.success ? (
        <Icon as={CheckCircle} weight="fill" color="green.400" boxSize="3.5" flexShrink={0} />
      ) : (
        <Icon as={XCircle} weight="fill" color="red.400" boxSize="3.5" flexShrink={0} />
      )}
      {req.statusCode && (
        <Text fontSize="11px" fontFamily="mono" color={req.success ? 'gray.600' : 'red.400'} flexShrink={0} w="24px" textAlign="right">
          {req.statusCode}
        </Text>
      )}
      <Text fontSize="11px" fontFamily="mono" color="gray.500" flexShrink={0} w="50px" textAlign="right">
        {formatMs(req.durationMs)}
      </Text>
    </Flex>
  );
}

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '4px',
  fontSize: '11px',
};

export const ApiMonitor: React.FC<ApiMonitorProps> = () => {
  const [preset, setPreset] = useState<TimePreset>('1h');
  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [selectedOps, setSelectedOps] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string[]>([]);
  const [bucketSize, setBucketSize] = useState<number>(1);
  const [chartType, setChartType] = useState<'area' | 'line'>('area');

  const isCustomRange = !!customDateRange.startDate && !!customDateRange.endDate;

  const resolvedOps = useMemo(() => {
    let ops = [...selectedOps];
    for (const groupKey of selectedGroup) {
      const group = ENDPOINT_GROUPS[groupKey];
      if (group) {
        for (const op of group.operations) {
          if (!ops.includes(op)) ops.push(op);
        }
      }
    }
    return ops;
  }, [selectedOps, selectedGroup]);

  const { startDate, endDate, activeBucket } = useMemo(() => {
    if (isCustomRange) {
      return {
        startDate: new Date(customDateRange.startDate).toISOString(),
        endDate: new Date(customDateRange.endDate).toISOString(),
        activeBucket: bucketSize,
      };
    }
    const cfg = PRESET_CONFIG[preset];
    const now = new Date();
    return {
      startDate: new Date(now.getTime() - cfg.ms).toISOString(),
      endDate: now.toISOString(),
      activeBucket: bucketSize,
    };
  }, [preset, customDateRange, isCustomRange, bucketSize]);

  const { data, loading, error } = useQuery<GetUpsApiMetricsData, GetUpsApiMetricsVars>(GET_UPS_API_METRICS, {
    variables: {
      startDate,
      endDate,
      operations: resolvedOps.length > 0 ? resolvedOps : undefined,
      bucketSizeMinutes: activeBucket,
    },
    fetchPolicy: 'network-only',
    pollInterval: 5000,
  });

  const metrics = data?.internalUPSApiMetrics;
  const live = metrics?.liveStatus;

  const filledBuckets = useMemo(() => {
    if (!metrics) return [];
    const bucketMs = activeBucket * 60 * 1000;
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const alignedStart = Math.floor(start / bucketMs) * bucketMs;
    const existingMap = new Map<number, (typeof metrics.buckets)[0]>();
    for (const b of metrics.buckets) {
      const t = Math.floor(new Date(b.timestamp).getTime() / bucketMs) * bucketMs;
      existingMap.set(t, b);
    }
    const result: (typeof metrics.buckets)[0][] = [];
    for (let t = alignedStart; t <= end; t += bucketMs) {
      const existing = existingMap.get(t);
      if (existing) {
        result.push(existing);
      } else {
        result.push({
          timestamp: new Date(t).toISOString(),
          totalRequests: 0,
          successCount: 0,
          failureCount: 0,
          avgDurationMs: 0,
          rateLimitHits: 0,
          queuedCount: 0,
        });
      }
    }
    return result;
  }, [metrics, activeBucket, startDate, endDate]);

  const handlePreset = (p: TimePreset) => {
    setPreset(p);
    setCustomDateRange({ startDate: '', endDate: '' });
    setBucketSize(PRESET_CONFIG[p].bucket);
  };

  const resetFilters = () => {
    setPreset('1h');
    setCustomDateRange({ startDate: '', endDate: '' });
    setSelectedOps([]);
    setSelectedGroup([]);
    setBucketSize(1);
  };

  const hasActiveFilters = selectedOps.length > 0 || selectedGroup.length > 0 || isCustomRange;

  const successRate =
    metrics && metrics.totalRequests > 0 ? ((metrics.totalRequests - metrics.totalErrors) / metrics.totalRequests) * 100 : 100;

  const errorRate = metrics && metrics.totalRequests > 0 ? (metrics.totalErrors / metrics.totalRequests) * 100 : 0;

  const avgDuration =
    metrics && metrics.buckets.length > 0
      ? Math.round(metrics.buckets.reduce((sum, b) => sum + b.avgDurationMs * b.totalRequests, 0) / Math.max(metrics.totalRequests, 1))
      : 0;

  const presetLabel = isCustomRange ? 'Custom' : PRESET_CONFIG[preset].label;

  if (error && !data) {
    return (
      <Layout fullWidth>
        <Box p="3" bg="red.900" rounded="md" borderWidth="1px" borderColor="red.700">
          <Flex gap="2" alignItems="center" mb="1">
            <Icon as={Warning} weight="fill" color="red.300" boxSize="4" />
            <Text color="red.200" fontWeight="medium" fontSize="sm">
              Failed to load UPS API metrics
            </Text>
          </Flex>
          <Text color="red.300" fontSize="xs" whiteSpace="pre-wrap">
            {error.message}
          </Text>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout fullWidth>
      {/* KPI Row */}
      <Grid templateColumns={['1fr 1fr', 'repeat(3, 1fr)', 'repeat(5, 1fr)']} gap="2" mb="2">
        <KpiCard
          icon={<Gauge size={14} weight="bold" />}
          label="Requests / Min"
          value={live ? `${live.production.rateLimitState.rate} / ${live.maxRate}` : '—'}
          sub={live ? `${ratePercent(live.production.rateLimitState.rate, live.maxRate)}% of limit` : ''}
          color={live ? (ratePercent(live.production.rateLimitState.rate, live.maxRate) >= 80 ? 'red.400' : 'white') : 'white'}
        />
        <KpiCard
          icon={<Lightning size={14} weight="bold" />}
          label="Inventory Req / Min"
          value={live ? `${live.production.rateLimitState.inventoryCount} / ${live.maxRate - live.reservedForScan}` : '—'}
          sub={live ? `${ratePercent(live.production.rateLimitState.inventoryCount, live.maxRate - live.reservedForScan)}% of limit` : ''}
        />
        <KpiCard
          icon={<Queue size={14} weight="bold" />}
          label="Queue Depth"
          value={live ? `${live.production.queueDepth} / ${live.maxRate}` : '—'}
          sub={
            live?.production.queueDepth === 0
              ? 'No pending jobs'
              : live?.production.oldestJobAge
                ? `Oldest: ${Math.round(live.production.oldestJobAge / 1000)}s`
                : ''
          }
        />
        <KpiCard
          icon={<ShieldWarning size={14} weight="bold" />}
          label="Error Rate"
          value={metrics ? `${errorRate.toFixed(1)}%` : '—'}
          sub={metrics ? `${metrics.totalErrors} errors` : ''}
          color={metrics && metrics.totalErrors > 0 ? 'red.400' : 'green.400'}
        />
        <KpiCard
          icon={<ShieldCheck size={14} weight="bold" />}
          label="Success Rate"
          value={metrics ? `${successRate.toFixed(1)}%` : '—'}
          sub={metrics ? `${metrics.totalRequests - metrics.totalErrors} successful` : ''}
          color={successRate >= 99 ? 'green.400' : successRate >= 95 ? 'yellow.400' : 'red.400'}
        />
      </Grid>

      {/* Filters Row */}
      <Flex gap="1.5" mb="2" alignItems="center" flexWrap="wrap">
        <FilterSelectPill
          label="Time Range"
          options={Object.entries(PRESET_CONFIG).map(([key, cfg]) => ({
            value: key,
            label: cfg.label,
          }))}
          initialValues={isCustomRange ? [] : [preset]}
          setValues={(vals) => {
            if (vals.length > 0) handlePreset(vals[vals.length - 1] as TimePreset);
          }}
          clear={() => handlePreset('1h')}
        />
        <FilterSelectPill
          label="Endpoint Group"
          options={Object.entries(ENDPOINT_GROUPS).map(([key, g]) => ({
            value: key,
            label: g.label,
          }))}
          initialValues={selectedGroup}
          setValues={setSelectedGroup}
          clear={() => setSelectedGroup([])}
        />
        <FilterSelectPill
          label="Operations"
          options={Object.entries(OPERATION_LABELS).map(([value, label]) => ({
            value,
            label: `${label}`,
          }))}
          initialValues={selectedOps}
          setValues={setSelectedOps}
          clear={() => setSelectedOps([])}
        />
        <FilterDateRangePill
          label="Custom Range"
          initialValues={customDateRange}
          setValues={(vals) => setCustomDateRange(vals)}
          clear={() => setCustomDateRange({ startDate: '', endDate: '' })}
        />

        {hasActiveFilters && (
          <Button size="xs" variant="ghost" color="gray.500" onClick={resetFilters} _hover={{ color: 'white' }} px="1.5">
            <Icon as={ArrowsClockwise} boxSize="3" mr="1" />
            Reset
          </Button>
        )}

        <Flex ml="auto" gap="1.5" alignItems="center">
          <Flex bg="gray.800" borderWidth="1px" borderColor="gray.700" rounded="md" overflow="hidden">
            {BUCKET_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="xs"
                variant="ghost"
                color={activeBucket === opt.value ? 'white' : 'gray.500'}
                bg={activeBucket === opt.value ? 'gray.700' : 'transparent'}
                rounded="none"
                fontWeight="medium"
                fontFamily="mono"
                fontSize="11px"
                onClick={() => setBucketSize(opt.value)}
                _hover={{ bg: 'gray.700', color: 'white' }}
                px="2"
                h="6"
              >
                {opt.label}
              </Button>
            ))}
          </Flex>
          <Badge colorScheme="green" fontSize="9px" fontFamily="mono" px="1.5" py="0.5" rounded="sm">
            <Flex alignItems="center" gap="1">
              <Pulse size={9} weight="bold" />
              AUTO-REFRESH 5S
            </Flex>
          </Badge>
        </Flex>
      </Flex>

      {loading && !data ? (
        <Flex justify="center" py="8">
          <Spinner size="md" color="gray.500" />
        </Flex>
      ) : metrics ? (
        <>
          {/* Main Chart + Summary Panel */}
          <Grid templateColumns={['1fr', '1fr', '5fr 2fr']} gap="2" mb="2">
            <Box {...cardProps}>
              <Flex justifyContent="space-between" alignItems="center" mb="2">
                <Flex alignItems="center" gap="2">
                  <SectionTitle>REQUESTS OVER TIME</SectionTitle>
                  <Badge colorScheme="gray" fontSize="9px" fontFamily="mono" variant="subtle" rounded="sm">
                    {presetLabel}
                  </Badge>
                </Flex>
                <Flex bg="gray.700" rounded="sm" overflow="hidden">
                  <Button
                    size="xs"
                    variant="ghost"
                    rounded="none"
                    h="5"
                    px="1.5"
                    fontSize="10px"
                    color={chartType === 'area' ? 'white' : 'gray.500'}
                    bg={chartType === 'area' ? 'gray.600' : 'transparent'}
                    onClick={() => setChartType('area')}
                    _hover={{ color: 'white' }}
                  >
                    <ChartLine size={10} />
                    <Text ml="1">Area</Text>
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    rounded="none"
                    h="5"
                    px="1.5"
                    fontSize="10px"
                    color={chartType === 'line' ? 'white' : 'gray.500'}
                    bg={chartType === 'line' ? 'gray.600' : 'transparent'}
                    onClick={() => setChartType('line')}
                    _hover={{ color: 'white' }}
                  >
                    <ChartLine size={10} />
                    <Text ml="1">Line</Text>
                  </Button>
                </Flex>
              </Flex>
              <ResponsiveContainer width="100%" height={220}>
                {chartType === 'area' ? (
                  <AreaChart data={filledBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} fontSize={10} stroke={CHART_COLORS.text} />
                    <YAxis fontSize={10} stroke={CHART_COLORS.text} />
                    <RechartsTooltip labelFormatter={(label) => new Date(label).toLocaleString('de-DE')} contentStyle={tooltipStyle} />

                    <Area
                      type="linear"
                      dataKey="totalRequests"
                      name="Total Requests"
                      stroke={CHART_COLORS.total}
                      fill={CHART_COLORS.total}
                      fillOpacity={0.15}
                      strokeWidth={1.5}
                    />
                    <Area
                      type="linear"
                      dataKey="successCount"
                      name="Successful"
                      stroke={CHART_COLORS.success}
                      fill={CHART_COLORS.success}
                      fillOpacity={0.3}
                      strokeWidth={1.5}
                    />
                    <Area
                      type="linear"
                      dataKey="failureCount"
                      name="Failed"
                      stroke={CHART_COLORS.failure}
                      fill={CHART_COLORS.failure}
                      fillOpacity={0.3}
                      strokeWidth={1.5}
                    />
                  </AreaChart>
                ) : (
                  <LineChart data={filledBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} fontSize={10} stroke={CHART_COLORS.text} />
                    <YAxis fontSize={10} stroke={CHART_COLORS.text} />
                    <RechartsTooltip labelFormatter={(label) => new Date(label).toLocaleString('de-DE')} contentStyle={tooltipStyle} />

                    <Line type="linear" dataKey="totalRequests" name="Total" stroke={CHART_COLORS.total} dot={false} strokeWidth={1.5} />
                    <Line
                      type="linear"
                      dataKey="successCount"
                      name="Successful"
                      stroke={CHART_COLORS.success}
                      dot={false}
                      strokeWidth={1.5}
                    />
                    <Line type="linear" dataKey="failureCount" name="Failed" stroke={CHART_COLORS.failure} dot={false} strokeWidth={1.5} />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </Box>

            <Box {...cardProps}>
              <Flex alignItems="center" gap="2" mb="2">
                <SectionTitle>REQUEST SUMMARY</SectionTitle>
                <Badge colorScheme="gray" fontSize="9px" fontFamily="mono" variant="subtle" rounded="sm">
                  {presetLabel}
                </Badge>
              </Flex>
              <Flex direction="column" gap="2">
                {[
                  { dot: 'gray.400', label: 'Total Requests', value: metrics.totalRequests.toLocaleString('de-DE'), color: 'white' },
                  {
                    dot: 'green.400',
                    label: 'Successful Requests',
                    value: `${(metrics.totalRequests - metrics.totalErrors).toLocaleString('de-DE')} (${successRate.toFixed(0)}%)`,
                    color: 'green.400',
                  },
                  {
                    dot: 'red.400',
                    label: 'Failed Requests',
                    value: `${metrics.totalErrors.toLocaleString('de-DE')} (${errorRate.toFixed(0)}%)`,
                    color: metrics.totalErrors > 0 ? 'red.400' : 'gray.600',
                  },
                  {
                    dot: 'yellow.400',
                    label: 'Rate Limit Hits',
                    value: `${metrics.totalRateLimitHits}`,
                    color: metrics.totalRateLimitHits > 0 ? 'yellow.400' : 'gray.600',
                  },
                ].map((row) => (
                  <Flex key={row.label} justifyContent="space-between" alignItems="center">
                    <Flex gap="1.5" alignItems="center">
                      <Box w="1.5" h="1.5" rounded="full" bg={row.dot} />
                      <Text fontSize="12px" color="gray.400">
                        {row.label}
                      </Text>
                    </Flex>
                    <Text fontSize="12px" fontWeight="medium" color={row.color} fontFamily="mono">
                      {row.value}
                    </Text>
                  </Flex>
                ))}

                <Box borderTop="1px solid" borderColor="gray.700" pt="2" mt="0.5">
                  <Flex justifyContent="space-between" alignItems="center" mb="1.5">
                    <Flex gap="1.5" alignItems="center">
                      <Icon as={Clock} boxSize="3" color="gray.500" />
                      <Text fontSize="12px" color="gray.400">
                        Avg Response Time
                      </Text>
                    </Flex>
                    <Text fontSize="12px" fontWeight="medium" color="white" fontFamily="mono">
                      {formatMs(avgDuration)}
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between" alignItems="center" mb="1.5">
                    <Text fontSize="12px" color="gray.400" pl="5">
                      P95 Response Time
                    </Text>
                    <Text fontSize="12px" fontWeight="medium" color="white" fontFamily="mono">
                      {formatMs(metrics.p95DurationMs)}
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="12px" color="gray.400" pl="5">
                      P99 Response Time
                    </Text>
                    <Text fontSize="12px" fontWeight="medium" color="white" fontFamily="mono">
                      {formatMs(metrics.p99DurationMs)}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          </Grid>

          {/* Bottom Row */}
          <Grid templateColumns={['1fr', '1fr', '1fr 1fr 1fr']} gap="2" mb="2">
            {/* Operations */}
            <Box {...cardProps}>
              <Flex alignItems="center" gap="2" mb="2">
                <SectionTitle>REQUESTS BY OPERATION</SectionTitle>
                <Badge colorScheme="gray" fontSize="9px" fontFamily="mono" variant="subtle" rounded="sm">
                  {presetLabel}
                </Badge>
              </Flex>
              {metrics.byOperation.length > 0 ? (
                <Table size="sm" variant="unstyled">
                  <Thead>
                    <Tr>
                      <Th
                        color="gray.600"
                        fontSize="9px"
                        fontFamily="mono"
                        textTransform="uppercase"
                        px="1"
                        pb="1.5"
                        borderBottom="1px solid"
                        borderColor="gray.700"
                      >
                        Operation
                      </Th>
                      <Th
                        color="gray.600"
                        fontSize="9px"
                        fontFamily="mono"
                        textTransform="uppercase"
                        px="1"
                        pb="1.5"
                        borderBottom="1px solid"
                        borderColor="gray.700"
                        isNumeric
                      >
                        Requests
                      </Th>
                      <Th
                        color="gray.600"
                        fontSize="9px"
                        fontFamily="mono"
                        textTransform="uppercase"
                        px="1"
                        pb="1.5"
                        borderBottom="1px solid"
                        borderColor="gray.700"
                        isNumeric
                      >
                        %
                      </Th>
                      <Th
                        color="gray.600"
                        fontSize="9px"
                        fontFamily="mono"
                        textTransform="uppercase"
                        px="1"
                        pb="1.5"
                        borderBottom="1px solid"
                        borderColor="gray.700"
                        isNumeric
                      >
                        Errors
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metrics.byOperation.map((op) => {
                      const barWidth = metrics.totalRequests > 0 ? (op.count / metrics.totalRequests) * 100 : 0;
                      const errors = op.count - Math.round((op.successRate / 100) * op.count);
                      return (
                        <Tr key={op.operation}>
                          <Td px="1" py="1">
                            <Flex alignItems="center" gap="1.5">
                              <Box w="1.5" h="1.5" rounded="sm" bg="blue.400" flexShrink={0} />
                              <Text fontSize="11px" color="gray.300" isTruncated>
                                {OPERATION_LABELS[op.operation] || op.operation}
                              </Text>
                            </Flex>
                            <Box mt="0.5" h="1.5px" bg="gray.700" rounded="full" w="full">
                              <Box h="1.5px" bg="blue.400" rounded="full" w={`${barWidth}%`} transition="width 300ms" />
                            </Box>
                          </Td>
                          <Td px="1" py="1" isNumeric>
                            <Text fontSize="11px" color="gray.300" fontFamily="mono">
                              {op.count}
                            </Text>
                          </Td>
                          <Td px="1" py="1" isNumeric>
                            <Text
                              fontSize="11px"
                              color={op.successRate >= 99 ? 'green.400' : op.successRate >= 95 ? 'yellow.400' : 'red.400'}
                              fontFamily="mono"
                            >
                              {op.successRate.toFixed(0)}%
                            </Text>
                          </Td>
                          <Td px="1" py="1" isNumeric>
                            <Text fontSize="11px" color={errors > 0 ? 'red.400' : 'gray.700'} fontFamily="mono">
                              {errors}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                    <Tr>
                      <Td px="1" py="1" borderTop="1px solid" borderColor="gray.700">
                        <Text fontSize="11px" fontWeight="medium" color="gray.400">
                          Total
                        </Text>
                      </Td>
                      <Td px="1" py="1" borderTop="1px solid" borderColor="gray.700" isNumeric>
                        <Text fontSize="11px" fontWeight="medium" color="white" fontFamily="mono">
                          {metrics.totalRequests}
                        </Text>
                      </Td>
                      <Td px="1" py="1" borderTop="1px solid" borderColor="gray.700" isNumeric>
                        <Text fontSize="11px" fontWeight="medium" color="white" fontFamily="mono">
                          100%
                        </Text>
                      </Td>
                      <Td px="1" py="1" borderTop="1px solid" borderColor="gray.700" isNumeric>
                        <Text
                          fontSize="11px"
                          fontWeight="medium"
                          color={metrics.totalErrors > 0 ? 'red.400' : 'gray.700'}
                          fontFamily="mono"
                        >
                          {metrics.totalErrors}
                        </Text>
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              ) : (
                <Flex justify="center" py="6">
                  <Text color="gray.600" fontSize="xs">
                    No data in this time range
                  </Text>
                </Flex>
              )}
            </Box>

            {/* Rate Limit */}
            <Box {...cardProps}>
              <Flex alignItems="center" gap="2" mb="2">
                <SectionTitle>RATE LIMIT & QUEUED REQUESTS</SectionTitle>
                <Badge colorScheme="gray" fontSize="9px" fontFamily="mono" variant="subtle" rounded="sm">
                  {presetLabel}
                </Badge>
              </Flex>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={filledBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                  <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} fontSize={9} stroke={CHART_COLORS.text} />
                  <YAxis fontSize={9} stroke={CHART_COLORS.text} />
                  <RechartsTooltip labelFormatter={(label) => new Date(label).toLocaleString('de-DE')} contentStyle={tooltipStyle} />
                  <Area
                    type="linear"
                    dataKey="rateLimitHits"
                    name="Rate Limit Hits"
                    stroke={CHART_COLORS.rateLimit}
                    fill={CHART_COLORS.rateLimit}
                    fillOpacity={0.3}
                    strokeWidth={1.5}
                  />
                  <Area
                    type="linear"
                    dataKey="queuedCount"
                    name="Queued Requests"
                    stroke={CHART_COLORS.queued}
                    fill={CHART_COLORS.queued}
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <Grid templateColumns="1fr 1fr" gap="2" mt="1.5">
                <Box>
                  <Text fontSize="9px" fontFamily="mono" color="gray.600" textTransform="uppercase" letterSpacing="wider">
                    Rate Limit Hits
                  </Text>
                  <Text
                    fontSize="md"
                    fontWeight="semibold"
                    fontFamily="mono"
                    color={metrics.totalRateLimitHits > 0 ? 'yellow.400' : 'white'}
                  >
                    {metrics.totalRateLimitHits}
                  </Text>
                  <Text fontSize="10px" color="gray.600">
                    {metrics.totalRequests > 0
                      ? `${((metrics.totalRateLimitHits / metrics.totalRequests) * 100).toFixed(1)}% of limit`
                      : '0% of limit'}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="9px" fontFamily="mono" color="gray.600" textTransform="uppercase" letterSpacing="wider">
                    Queued Requests (Max)
                  </Text>
                  <Text fontSize="md" fontWeight="semibold" fontFamily="mono" color="white">
                    {Math.max(0, ...filledBuckets.map((b) => b.queuedCount))}
                  </Text>
                  <Text fontSize="10px" color="gray.600">
                    Max in period
                  </Text>
                </Box>
              </Grid>
            </Box>

            {/* Response Time */}
            <Box {...cardProps}>
              <Flex alignItems="center" gap="2" mb="2">
                <SectionTitle>RESPONSE TIME (MS)</SectionTitle>
              </Flex>
              <ResponsiveContainer width="100%" height={150}>
                <AreaChart data={filledBuckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                  <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} fontSize={9} stroke={CHART_COLORS.text} />
                  <YAxis fontSize={9} stroke={CHART_COLORS.text} />
                  <RechartsTooltip
                    labelFormatter={(label) => new Date(label).toLocaleString('de-DE')}
                    contentStyle={tooltipStyle}
                    formatter={(value: number) => [`${value} ms`, 'Avg']}
                  />
                  <Area
                    type="linear"
                    dataKey="avgDurationMs"
                    name="Avg Response Time"
                    stroke={CHART_COLORS.avgDuration}
                    fill={CHART_COLORS.avgDuration}
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <Grid templateColumns="1fr 1fr 1fr" gap="2" mt="1.5">
                {[
                  { label: 'Avg', value: avgDuration },
                  { label: 'P95', value: metrics.p95DurationMs },
                  { label: 'P99', value: metrics.p99DurationMs },
                ].map((s) => (
                  <Box key={s.label}>
                    <Text fontSize="9px" fontFamily="mono" color="gray.600" textTransform="uppercase" letterSpacing="wider">
                      {s.label}
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color="white" fontFamily="mono">
                      {formatMs(s.value)}
                    </Text>
                  </Box>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Recent Requests + Recent Errors */}
          <Grid templateColumns={['1fr', '1fr', '1fr 1fr']} gap="2" mb="2">
            {/* Live Ops Feed */}
            <Box {...cardProps} p="0" overflow="hidden">
              <Flex alignItems="center" justifyContent="space-between" px="2.5" py="1.5" borderBottom="1px solid" borderColor="gray.700">
                <Flex alignItems="center" gap="1.5">
                  <Box w="1.5" h="1.5" rounded="full" bg="green.400" />
                  <SectionTitle>LIVE OPS</SectionTitle>
                  <Text fontSize="10px" color="gray.600" fontFamily="mono">
                    Last {metrics.recentRequests.length} requests
                  </Text>
                </Flex>
              </Flex>
              <Box
                maxH="320px"
                overflowY="auto"
                sx={{
                  '&::-webkit-scrollbar': { width: '3px' },
                  '&::-webkit-scrollbar-track': { bg: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { bg: 'gray.700', rounded: 'full' },
                }}
              >
                {metrics.recentRequests.length > 0 ? (
                  metrics.recentRequests.map((req) => <LiveRequestRow key={req.id} req={req} />)
                ) : (
                  <Flex justify="center" py="6">
                    <Text color="gray.600" fontSize="xs">
                      No requests recorded yet
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>

            {/* Recent Errors */}
            <Box {...cardProps}>
              <SectionTitle>RECENT ERRORS</SectionTitle>
              {metrics.recentErrors.length > 0 ? (
                <Table size="sm" variant="unstyled" mt="2">
                  <Thead>
                    <Tr>
                      {['', 'Operation', 'Error', 'Status Code', 'Count', 'Last Occurred'].map((h, i) => (
                        <Th
                          key={h}
                          color="gray.600"
                          fontSize="9px"
                          fontFamily="mono"
                          textTransform="uppercase"
                          px="1.5"
                          pb="1.5"
                          borderBottom="1px solid"
                          borderColor="gray.700"
                          isNumeric={i === 3 || i === 4}
                        >
                          {h}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metrics.recentErrors.map((err, idx) => (
                      <Tr key={idx} _hover={{ bg: 'whiteAlpha.50' }}>
                        <Td px="1.5" py="1">
                          <Icon as={XCircle} weight="fill" color="red.400" boxSize="3" />
                        </Td>
                        <Td px="1.5" py="1">
                          <Text fontSize="11px" color="gray.300">
                            {OPERATION_LABELS[err.operation] || err.operation}
                          </Text>
                        </Td>
                        <Td px="1.5" py="1">
                          <Text fontSize="11px" color="red.300" isTruncated maxW="250px">
                            {err.errorCode || err.errorMessage || 'Unknown'}
                          </Text>
                        </Td>
                        <Td px="1.5" py="1" isNumeric>
                          <Text fontSize="11px" color="gray.500" fontFamily="mono">
                            {err.statusCode || '—'}
                          </Text>
                        </Td>
                        <Td px="1.5" py="1" isNumeric>
                          <Text fontSize="11px" color="red.400" fontFamily="mono" fontWeight="medium">
                            {err.count}
                          </Text>
                        </Td>
                        <Td px="1.5" py="1">
                          <Text fontSize="11px" color="gray.600" fontFamily="mono">
                            {new Date(err.lastOccurred).toLocaleString('de-DE')}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Flex justify="center" py="4" direction="column" alignItems="center" gap="1" mt="1">
                  <Icon as={CheckCircle} weight="fill" color="green.400" boxSize="5" />
                  <Text color="gray.600" fontSize="xs">
                    No errors in this time range
                  </Text>
                </Flex>
              )}
            </Box>
          </Grid>
        </>
      ) : null}
    </Layout>
  );
};
